<template>
  <content-with-sidebar data-v-sticky-container>
    <div class="blog-detail-wrapper">
      <b-row>
        <b-col cols="12">
          <b-spinner
            v-if="loading"
            class="align-middle mr-1 my-2"
            variant="secondary"
          ></b-spinner>
          <b-card>
            <template #header>
              <div
                class="product-header d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100"
              >
                <h4 class="m-0 text-dark font-weight-bold">
                  {{ descTitle }}
                </h4>
                <b-button
                  v-b-tooltip.hover
                  :variant="isBookmarked ? 'success' : 'outline-primary'"
                  class="bookmark-btn mt-1 mt-md-0 d-inline-flex align-items-center"
                  :title="isBookmarked ? 'Delete bookmark' : 'Save to bookmark'"
                  :disabled="bookmarkLoading"
                  @click="toggleBookmark"
                >
                  <b-icon-bookmark-fill
                    v-if="isBookmarked"
                    class="bookmark-icon"
                  ></b-icon-bookmark-fill>
                  <b-icon-bookmark
                    v-else
                    class="bookmark-icon"
                  ></b-icon-bookmark>
                  <span class="bookmark-label">{{ bookmarkLabel }}</span>
                </b-button>
              </div>
            </template>
            <b-card-text>
              <div class="product-hero">
                <div class="hero-media">
                  <div
                    v-if="loading"
                    class="product-image-placeholder skeleton-box hero-img"
                  ></div>
                  <img
                    v-else
                    :src="productImage"
                    alt="Product preview"
                    class="product-image hero-img"
                  />
                  <div class="hero-overlay"></div>
                </div>

                <div class="hero-footer">
                  <div class="hero-meta">
                    <span class="chip">Product</span>
                    <span v-if="!loading" class="chip">
                      Updated
                      {{ $moment(lastUpdate).format('DD MMM YYYY, HH:mm') }}
                    </span>
                    <span v-else class="chip" style="width: 120px;">
                      <span
                        class="skeleton-box w-100"
                        style="height: 10px;"
                      ></span>
                    </span>
                  </div>
                </div>
              </div>

              <div class="info-card mt-2">
                <div class="info-card__title">Product Overview</div>
                <SkeletonContainer v-if="loading" />
                <p
                  v-else
                  class="txt-md font-weight-light mb-0"
                  v-html="desc"
                ></p>
              </div>

              <div v-if="benefit !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <img class="px-50 w-100" :src="imgBenefit" />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        Benefit
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="benefit"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="valueProposition !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <img class="px-100 w-100" :src="imgValueProposition" />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        Value Proposition
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="valueProposition"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="salesProposition !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <feather-icon icon="TrendingUpIcon" size="18" />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        Sales Proposition
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="salesProposition"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="specificationFeatures !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <img
                          class="px-100 w-100"
                          :src="imgSpecificationFeatures"
                        />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        Specification/Features
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="specificationFeatures"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="stp !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <img class="px-50 w-100" :src="imgStp" />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        STP Pelanggan
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="stp"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="config !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <img class="px-50 w-100" :src="imgConfig" />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        Konfigurasi
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="config"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div v-if="sla !== '-'" class="section-panel">
                <b-row>
                  <b-col md="12">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <b-avatar variant="primary" class="mr-1 ava-pink">
                        <img class="px-50 w-100" :src="imgSla" />
                      </b-avatar>
                      <div
                        class="blog-category-title font-weight-bolder txt-dark"
                      >
                        SLA / SLG
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col md="12" class="mb-2 pl-5">
                    <div
                      class="d-flex justify-content-start align-items-center mb-75"
                    >
                      <div
                        class="blog-category-title txt-dark font-weight-light"
                        v-html="sla"
                      ></div>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>

      <!-- document -->
      <b-row class="mt-2">
        <b-col cols="12">
          <b-card title="Document">
            <div class="doc-list">
              <div v-if="hasVideo" class="doc-item">
                <div class="doc-left">
                  <div class="doc-icon">
                    <feather-icon size="16" icon="PlayCircleIcon" />
                  </div>
                  <div>
                    <div class="doc-title">{{ videoDisplayName }}</div>
                    <div class="doc-meta">{{ videoSizeLabel }}</div>
                  </div>
                </div>
                <div class="doc-actions">
                  <b-button
                    size="sm"
                    variant="primary"
                    @click="openVideoPreview"
                  >
                    <feather-icon size="14" icon="EyeIcon" class="mr-25" />
                    Preview
                  </b-button>
                </div>
              </div>
              <div
                v-for="data in documentsVisible"
                :key="data.__idx"
                class="doc-item"
              >
                <div class="doc-left">
                  <div class="doc-icon">
                    <feather-icon size="16" icon="FileTextIcon" />
                  </div>
                  <div>
                    <div class="doc-title">{{ data.usrName }}</div>
                    <div class="doc-meta">
                      {{ toMB(data.size) }} MB •
                      {{ data.created_at ? data.created_at : '1 Jan 2021' }}
                    </div>
                  </div>
                </div>
                <div class="doc-actions">
                  <b-button
                    v-tracker="'button:download-reference:' + data.usrName"
                    size="sm"
                    variant="primary"
                    @click="openReferenceLink(data.__idx)"
                  >
                    <b-spinner
                      v-if="referencesLoading.includes(data.__idx)"
                      small
                    />
                    <template v-else>
                      <feather-icon size="14" icon="EyeIcon" class="mr-25" />
                      Preview
                    </template>
                  </b-button>
                </div>
              </div>
              <div
                v-if="!hasDocumentContent"
                class="text-center text-muted py-1"
              >
                No document available
              </div>
              <div
                v-else-if="documentsVisible.length < referensi_product.length"
                class="text-center mt-1"
              >
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="loadMoreDocuments"
                >
                  Load more documents
                </b-button>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-modal
        ref="modal-video-preview"
        :title="videoDisplayName"
        size="xl"
        centered
        @hidden="handleVideoPreviewHidden"
      >
        <div v-if="videoPreview.loading" class="text-center py-2">
          <b-spinner variant="primary" />
        </div>

        <div
          v-else-if="videoPreview.error"
          class="text-center text-danger py-2"
        >
          {{ videoPreview.error }}
        </div>

        <video
          v-else
          ref="videoPreviewPlayer"
          controls
          preload="metadata"
          class="w-100 video-preview-player"
          :src="videoPreview.url"
          @error="onVideoPreviewError"
        ></video>

        <template #modal-footer>
          <b-button
            variant="outline-secondary"
            @click="$refs['modal-video-preview'].hide()"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="videoLoading.includes('video-download')"
            @click="downloadVideoPreview"
          >
            <b-spinner
              v-if="videoLoading.includes('video-download')"
              small
              class="mr-50"
            />
            Download
          </b-button>
        </template>
      </b-modal>

      <b-modal
        ref="modal-document-preview"
        :title="preview.title || 'Document Preview'"
        size="xl"
        centered
        @hidden="clearPreview"
      >
        <div v-if="preview.loading" class="text-center py-2">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="preview.error" class="text-center text-danger py-2">
          {{ preview.error }}
        </div>

        <div v-else>
          <iframe
            v-if="preview.kind === 'pdf'"
            class="preview-frame"
            :src="preview.url"
            title="Document preview"
          ></iframe>

          <iframe
            v-else-if="preview.kind === 'office' && preview.officeUrl"
            class="preview-frame"
            :src="preview.officeUrl"
            title="Office document preview"
          ></iframe>

          <div v-else-if="preview.kind === 'pptx'" class="pptx-preview-wrapper">
            <div
              ref="pptxPreviewContainer"
              class="pptx-preview-container"
            ></div>
          </div>

          <div v-else-if="preview.kind === 'docx'" class="docx-preview-wrapper">
            <div
              ref="docxPreviewContainer"
              class="docx-preview-container"
            ></div>
          </div>

          <div v-else-if="preview.kind === 'image'" class="text-center">
            <img
              :src="preview.url"
              alt="Preview"
              class="img-fluid preview-image"
            />
          </div>

          <div v-else class="text-center text-muted py-2">
            Preview tidak tersedia untuk tipe file ini.
          </div>
        </div>

        <template #modal-footer>
          <b-button
            variant="outline-secondary"
            @click="$refs['modal-document-preview'].hide()"
          >
            Close
          </b-button>
          <b-button
            variant="primary"
            :disabled="!preview.blob"
            @click="downloadPreview"
          >
            Download
          </b-button>
        </template>
      </b-modal>

      <b-row class="mt-2">
        <b-col cols="12">
          <b-card>
            <template #header>
              <div
                class="d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100"
              >
                <div>
                  <h4 class="m-0">Testimonial</h4>
                  <small v-if="testimonials.length" class="text-muted">
                    {{ testimonials.length }}
                    testimonial{{ testimonials.length > 1 ? 's' : '' }}
                  </small>
                  <small v-else class="text-muted"
                    >Service user experience stories</small
                  >
                </div>
              </div>
            </template>

            <div class="testimonial-list">
              <div
                v-for="(item, index) in visibleTestimonials"
                :key="item.id || index"
                class="testimonial-item"
              >
                <div class="testimonial-item__avatar">
                  <img
                    v-if="item.logoUrl"
                    :src="item.logoUrl"
                    :alt="item.customer"
                    class="testimonial-item__logo"
                  />
                  <span v-else>{{ item.initial }}</span>
                </div>
                <div class="testimonial-item__body">
                  <div class="testimonial-item__top">
                    <div class="testimonial-item__name">
                      {{ item.customer }}
                    </div>
                    <div class="testimonial-item__date">
                      {{ item.createdAtLabel || '-' }}
                    </div>
                  </div>
                  <p class="testimonial-item__message">{{ item.message }}</p>
                </div>
              </div>

              <div
                v-if="testimonials.length > testimonialPreviewCount"
                class="text-center mt-1"
              >
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="showAllTestimonials = !showAllTestimonials"
                >
                  {{
                    showAllTestimonials
                      ? 'Show less'
                      : `Show more (${testimonials.length -
                          testimonialPreviewCount})`
                  }}
                </b-button>
              </div>

              <div
                v-if="!testimonials.length"
                class="text-center text-muted py-1"
              >
                <feather-icon
                  icon="MessageSquareIcon"
                  size="16"
                  class="mr-50"
                />
                No testimonials available for this product.
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <div slot="sidebar" v-sticky="options" class="blog-sidebar">
      <div
        class="blog-categories sidebar-card"
        :class="{ topstick: !showNavbar }"
      >
        <b-button
          v-if="!showNavbar"
          variant="danger"
          class="d-flex d-lg-none align-items-center mb-1 ml-auto"
          aria-controls="collapse-categories"
          @click="isCategoriesVisible = !isCategoriesVisible"
        >
          <span>Links</span>
          <feather-icon
            :icon="isCategoriesVisible ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="16"
            class="ml-auto"
          />
        </b-button>

        <b-collapse
          id="collapse-categories"
          v-model="isCategoriesVisible"
          class="d-lg-block links-container"
        >
          <div v-tracker="'button:faq'" class="link-row">
            <b-link :href="faqHref" @click.prevent="faq()">
              <div class="link-icon">
                <feather-icon icon="MessageSquareIcon" size="16" />
              </div>
            </b-link>
            <b-link :href="faqHref" @click.prevent="faq()">
              <div class="blog-category-title text-body">FAQ</div>
            </b-link>
          </div>

          <div v-tracker="'button:tarif'" class="link-row">
            <b-link :href="tarifHref" @click.prevent="tarif()">
              <div class="link-icon rpIcon">
                Rp
              </div>
            </b-link>
            <b-link :href="tarifHref" @click.prevent="tarif()">
              <div class="blog-category-title text-body">Tarif</div>
            </b-link>
          </div>

          <div id="proposal" class="link-row">
            <b-spinner v-show="loadingGenerateProposal"> </b-spinner>
            <b-link v-if="!loadingGenerateProposal">
              <div class="link-icon">
                <feather-icon icon="FileTextIcon" size="16" />
              </div>
            </b-link>
            <b-link
              v-if="!loadingGenerateProposal"
              v-tracker="'button:create-proposal'"
            >
              <div class="blog-category-title text-body">
                Create Product Summary
              </div>
            </b-link>
          </div>
          <b-popover target="proposal" placement="bottom" triggers="click">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              v-tracker="'button:create-proposal:default'"
              variant="flat-secondary"
              class="d-flex"
              @click="
                $refs['modal-file-default-type'].show()
                $root.$emit('bv::hide::popover', 'proposal')
              "
            >
              Default
            </b-button>
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              v-tracker="'button:create-proposal:select-attribute'"
              variant="flat-secondary"
              class="d-flex"
              style="width: 100%;"
              @click="
                $refs['modal-attribute'].show()
                $root.$emit('bv::hide::popover', 'proposal')
              "
            >
              Select Attribute
            </b-button>
          </b-popover>
          <b-modal
            ref="modal-attribute"
            centered
            title="Vertically Centered"
            :hide-footer="true"
            :hide-header="true"
            size="xs"
          >
            <b-form-group style="width: 100%;">
              <b-form-checkbox
                v-for="option in proposalOptions"
                :key="option.val"
                v-model="proposalSelectedFields"
                :value="option.val"
                style="width: 100%;"
              >
                {{ option.label | capitalizeFirstLetter }}
              </b-form-checkbox>
            </b-form-group>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-tracker="'button:create-proposal:generate'"
              style="width: 100%;"
              variant="danger"
              @click="
                $refs['modal-file-type'].show()
                $refs['modal-attribute'].hide()
              "
            >
              Generate
            </b-button>
          </b-modal>
          <b-modal
            ref="modal-file-type"
            centered
            title="Vertically Centered"
            :hide-footer="true"
            :hide-header="true"
            size="xs"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-tracker="'button:create-proposal:generate:pdf'"
              style="width: 100%;"
              variant="danger"
              class="mb-1"
              @click="
                generate($route.params.key, proposalSelectedFields, descTitle)
                $refs['modal-file-type'].hide()
              "
            >
              Genereta to PDF
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-tracker="'button:create-proposal:generate:word'"
              style="width: 100%;"
              variant="success"
              @click="
                generateWord(
                  $route.params.key,
                  proposalSelectedFields,
                  descTitle
                )
                $refs['modal-file-type'].hide()
              "
            >
              Generate to Word <br /><small style="font-style:italic;"
                >(Image cannot be rendered on word)</small
              >
            </b-button>
          </b-modal>
          <b-modal
            ref="modal-file-default-type"
            centered
            title="Vertically Centered"
            :hide-footer="true"
            :hide-header="true"
            size="xs"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-tracker="'button:create-proposal:generate:pdf'"
              style="width: 100%;"
              variant="danger"
              class="mb-1"
              @click="
                generate($route.params.key, ['all'], descTitle)
                $refs['modal-file-default-type'].hide()
              "
            >
              Genereta to PDF
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-tracker="'button:create-proposal:generate:word'"
              style="width: 100%;"
              variant="success"
              @click="
                generateWord($route.params.key, ['all'], descTitle)
                $refs['modal-file-default-type'].hide()
              "
            >
              Generate to Word <br /><small style="font-style:italic;"
                >(Image cannot be rendered on word)</small
              >
            </b-button>
          </b-modal>
          <div v-tracker="'button:compare'" class="link-row">
            <b-link :href="compareHref" @click.prevent="compareProduct()">
              <div class="link-icon">
                <feather-icon icon="ColumnsIcon" size="16" />
              </div>
            </b-link>
            <b-link :href="compareHref" @click.prevent="compareProduct()">
              <div class="blog-category-title text-body">Compare Product</div>
            </b-link>
          </div>

          <div v-tracker="'button:case-study'" class="link-row">
            <b-link :href="caseHref" @click.prevent="casestudy()">
              <div class="link-icon">
                <feather-icon icon="ClipboardIcon" size="16" />
              </div>
            </b-link>
            <b-link :href="caseHref" @click.prevent="casestudy()">
              <div class="blog-category-title text-body">Case Study</div>
            </b-link>
          </div>

          <div v-tracker="'button:proposal-list'" class="link-row">
            <b-link
              :href="`/product/proposal/${$route.params.key}`"
              @click.prevent="proposal()"
            >
              <div class="link-icon">
                <feather-icon icon="FileTextIcon" size="16" />
              </div>
            </b-link>
            <b-link
              :href="`/product/proposal/${$route.params.key}`"
              @click.prevent="proposal()"
            >
              <div class="blog-category-title text-body">Proposal</div>
            </b-link>
          </div>

          <div v-tracker="'button:sharing-session'" class="link-row">
            <b-link>
              <div class="link-icon">
                <feather-icon icon="Share2Icon" size="16" />
              </div>
            </b-link>
            <b-link
              :to="{ name: 'list-pcc', params: { product: $route.params.key } }"
            >
              <div class="blog-category-title text-body">Sharing Session</div>
            </b-link>
          </div>
        </b-collapse>
      </div>

      <div class="blog-recent-posts mt-3">
        <b-modal
          ref="modal-request-access"
          centered
          title="Vertically Centered"
          :hide-footer="true"
          :hide-header="true"
          size="md"
        >
          <div class="d-flex flex-column align-items-center">
            <div class="align-self-end">
              <b-button
                v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                variant="flat-secondary"
                class="btn-icon"
                size="md"
                @click="$refs['modal-request-access'].hide()"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
            <div class="h2 text-danger mb-2">
              Kamu Tidak Mempunyai Hak Akses!
            </div>
            <b-card-text class="mb-2">
              <p>
                Untuk saat ini kamu tidak mempunyai hak akses untuk menggunakan
                fitur ini
              </p>
            </b-card-text>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              style="width: 100%;"
              variant="success"
              class="mb-2"
              :disabled="alreadyRequest === 1"
              @click="requestAccess()"
            >
              {{
                alreadyRequest ? 'Kamu sudah request akses' : 'Request akses'
              }}
            </b-button>
          </div>
        </b-modal>
        <b-modal
          ref="modal-dev"
          centered
          title="Vertically Centered"
          :hide-footer="true"
          :hide-header="true"
          size="md"
        >
          <div class="d-flex flex-column align-items-center">
            <div class="align-self-end">
              <b-button
                v-ripple.400="'rgba(40, 199, 111, 0.15)'"
                variant="flat-secondary"
                class="btn-icon"
                size="md"
                @click="$refs['modal-dev'].hide()"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
            <div class="h2 text-danger mb-2">
              Notice
            </div>
            <b-card-text class="mb-2">
              <p>
                Feature "Proposal" currently under development
              </p>
            </b-card-text>
          </div>
        </b-modal>
      </div>
    </div>
  </content-with-sidebar>
</template>

<script>
import {
  BFormInput,
  BMedia,
  BAvatar,
  BMediaAside,
  BMediaBody,
  BImg,
  BLink,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BEmbed,
  BTabs,
  BTab,
  BCard,
  BRow,
  BCol,
  BBadge,
  BCardText,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BModal,
  VBPopover,
  BPopover,
  BSpinner,
  BCollapse,
  BIconBookmark,
  BIconBookmarkFill,
  BCarousel,
  BCarouselSlide,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { kFormatter } from '@core/utils/filter'
import ContentWithSidebar from '@core/layouts/components/content-with-sidebar/ContentWithSidebar.vue'
import * as Services from './services'
import SkeletonContainer from './components/SkeletonContainer.vue'

export default {
  components: {
    BFormInput,
    BMedia,
    BEmbed,
    BAvatar,
    BMediaAside,
    BMediaBody,
    BLink,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BImg,
    BBadge,
    BCardText,
    BDropdown,
    BForm,
    BTabs,
    BTab,
    BDropdownItem,
    BFormTextarea,
    BFormCheckbox,
    BModal,
    BButton,
    ContentWithSidebar,
    BPopover,
    BSpinner,
    BCollapse,
    SkeletonContainer,
    BIconBookmark,
    BIconBookmarkFill,
    BCarousel,
    BCarouselSlide,
  },
  directives: {
    Ripple,
    'b-popover': VBPopover,
  },
  setup() {
    const { Proposal, ProposalWord } = Services
    const {
      generate,
      loading: loadingGenerateProposal,
      availableFields: proposalOptions,
      selectedFields: proposalSelectedFields,
    } = Proposal()
    const { generateWord } = ProposalWord()
    return {
      generate,
      generateWord,
      loadingGenerateProposal,
      proposalOptions,
      proposalSelectedFields,
    }
  },
  data() {
    return {
      faqHref: '',
      tarifHref: '',
      compareHref: '',
      caseHref: '',
      recordNavContext: '',
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      showNavbar: true,
      lastScrollPosition: 0,
      descTitle: '',
      lastUpdate: '',
      desc: '',
      benefit: '',
      imgBenefit: require('@/assets/images/products/benefit.png'),
      defaultProductImage: require('@/assets/images/product_banner.png'),
      productImage: require('@/assets/images/product_banner.png'),
      valueProposition: '',
      salesProposition: '',
      imgValueProposition: require('@/assets/images/products/ValuePropotition.png'),
      specificationFeatures: '',
      imgSpecificationFeatures: require('@/assets/images/products/SpecificationFeatures.png'),
      stp: '',
      imgStp: require('@/assets/images/products/stp.png'),
      config: '',
      imgConfig: require('@/assets/images/products/konfigurasi.png'),
      sla: '',
      imgSla: require('@/assets/images/products/sla.png'),
      video: '',
      videoItem: null,
      videoLoading: [],
      videoPreview: {
        loading: false,
        error: '',
        url: '',
        blob: null,
        filename: '',
        mime: '',
      },
      videoPreviewRequestId: 0,
      videoPreviewRetried: false,
      productPhotos: [],
      contacts_pi: [],
      contacts_com: [],
      referensi_product: [],
      proposalFiles: [],
      blogSidebar: {
        recentPosts: [
          {
            id: 7,
            img: require('@/assets/images/product_banner.png'),
            title: 'Why Should Forget Facebook?',
            createdTime: 'Jan 14 2020',
          },
        ],
        categories: [
          { category: 'Fashion', icon: 'WatchIcon' },
          { category: 'Food', icon: 'ShoppingCartIcon' },
          { category: 'Gaming', icon: 'CommandIcon' },
          { category: 'Quote', icon: 'HashIcon' },
          { category: 'Video', icon: 'VideoIcon' },
        ],
      },
      socialShareIcons: [
        'GithubIcon',
        'GitlabIcon',
        'FacebookIcon',
        'TwitterIcon',
        'LinkedinIcon',
      ],
      testimonials: [],
      showAllTestimonials: false,
      testimonialPreviewCount: 3,
      options: {
        topSpacing: 0,
        bottomSpacing: 0,
        containerSelector: '[data-v-sticky-container]',
        innerWrapperSelector: '[data-v-sticky-inner]',
        resizeSensor: true, // [1] read above
        stickyClass: 'is-affixed',
        minWidth: 0,
      },
      telegramIcon: require('@/assets/images/icons/telegram-icon.svg'),
      whatsappIcon: require('@/assets/images/icons/whatsapp-icon.svg'),
      alreadyRequest: 0,
      loading: true,
      referencesLoading: [],
      proposalLoading: [],
      isCategoriesVisible: false,
      isBookmarked: false,
      bookmarkLoading: false,
      documentVisibleCount: 5,
      backendBookmark: null,
      previewRequestId: 0,
      pptxPreviewInit: null,
      pptxPreviewer: null,
      docxRenderAsync: null,
      preview: {
        loading: false,
        error: '',
        url: '',
        officeUrl: '',
        sourceUrl: '',
        blob: null,
        title: '',
        filename: '',
        kind: '',
        mime: '',
      },
      childProducts: [],
      brokenChildThumbnails: {},
      showAllChildren: false,
      childPreviewCount: 3,
      defaultProposalOptions: [],
      slide: 0,
      sliding: null,
    }
  },
  computed: {
    documentsVisible() {
      return (this.referensi_product || [])
        .map((item, idx) => ({ ...item, __idx: idx }))
        .slice(0, this.documentVisibleCount)
    },
    hasVideo() {
      return typeof this.video === 'string' && this.video.trim().length > 0
    },
    hasDocumentContent() {
      return this.hasVideo || (this.referensi_product || []).length > 0
    },
    bookmarkLabel() {
      return this.isBookmarked ? 'Bookmarked' : 'Save to Bookmark'
    },
    isApprover() {
      const { permissions } = this.$store.getters['oauth/getActiveUser']
      if (!permissions.includes('aprove_product')) {
        return false
      }
      return true
    },
    canTarif() {
      const { permissions } = this.$store.getters['oauth/getActiveUser']
      if (!permissions.includes('calculator_tarif')) {
        return false
      }
      return true
    },
    videoDisplayName() {
      const item = this.videoItem || {}
      const explicitName =
        item?.usrName ||
        item?.title ||
        item?.display_name ||
        item?.displayName ||
        item?.label
      if (
        explicitName &&
        String(explicitName).trim() &&
        !this.isAttachmentPathLike(explicitName)
      ) {
        return String(explicitName).trim()
      }
      const fallbackPath =
        item?.name || item?.link || item?.file_url || item?.path || this.video
      return this.extractAttachmentDisplayName(fallbackPath) || 'Product Video'
    },
    videoSizeLabel() {
      return this.formatFileSize(this.videoItem?.size)
    },
    hasChildProducts() {
      return Array.isArray(this.childProducts) && this.childProducts.length > 0
    },
    visibleChildProducts() {
      if (this.showAllChildren) {
        return this.childProducts
      }
      return this.childProducts.slice(0, this.childPreviewCount)
    },
    isMobileView() {
      return this.viewportWidth < 768
    },
    visibleTestimonials() {
      if (this.showAllTestimonials) return this.testimonials
      return this.testimonials.slice(0, this.testimonialPreviewCount)
    },
  },
  watch: {
    '$route.params.key': function(newKey, oldKey) {
      if (!newKey || newKey === oldKey) return
      this.showAllChildren = false
      this.showAllTestimonials = false
      this.brokenChildThumbnails = {}
      this.documentVisibleCount = 5
      this.loadProductDetail()
      this.recordProductView()
      this.syncInitialBookmark()
      this.applyQuickLinks()
    },
  },
  created() {
    this.loadProductDetail()
    this.recordProductView()
    this.applyQuickLinks()
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
    window.addEventListener('resize', this.onResize)
    if (this.isAlreadyRequest()) {
      this.alreadyRequest = 1
    } else {
      this.alreadyRequest = 0
    }
    this.syncInitialBookmark()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    this.clearPreview()
    this.stopVideoPreview()
    this.clearVideoPreview()
  },
  methods: {
    onSlideStart(slide) {
      this.sliding = true
    },
    onSlideEnd(slide) {
      this.sliding = false
    },
    onResize() {
      this.viewportWidth = window.innerWidth
    },
    getNameInitial(value = '') {
      const cleaned = String(value || '').trim()
      if (!cleaned) return 'NA'
      const parts = cleaned.split(/\s+/).filter(Boolean)
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
    },
    normalizeTestimonial(item = {}) {
      const customer = String(item.customer || '').trim()
      return {
        id: item.id,
        customer,
        initial: this.getNameInitial(customer),
        logoUrl: item.logo_url || '',
        createdAt: item.created_at || null,
        createdAtLabel: item.created_at
          ? this.$moment(item.created_at).format('DD MMM YYYY, HH:mm')
          : '-',
        message: String(item.testimonial || '').trim(),
      }
    },
    mapTestimonials(items) {
      if (!Array.isArray(items)) return []
      return items.map(item => this.normalizeTestimonial(item))
    },
    fetchTestimonials() {
      this.testimonials = []
      this.$http
        .get(`/product/testimonial/${this.$route.params.key}`)
        .then(({ data }) => {
          this.testimonials = this.mapTestimonials(data?.testimonials)
        })
        .catch(() => {
          this.testimonials = []
        })
    },
    recordProductView() {
      this.$http
        .post('/product-view-log', {
          product_code: this.$route.params.key,
        })
        .catch(() => {})
    },
    loadProductDetail() {
      this.loading = true
      this.brokenChildThumbnails = {}
      this.testimonials = []
      if (this.defaultProposalOptions.length > 0) {
        this.proposalOptions = [...this.defaultProposalOptions]
      }

      const mapRequest = {
        map: {
          KODE: 's',
          PRODUCT: 's',
          LAST_UPDATE: 'd',
          PROD_DESCRIPTION: 'd',
          PROD_BENEFIT: 'd',
          VALUE_PROPOSITION: 'd',
          SALES_PROPOSITION: 'd',
          PROD_FEATURES: 'd',
          PROD_CATEGORY: 'd',
          product_category_details: 'd',
          PROD_CUST_STP: 'd',
          PROD_CONFIG: 'd',
          PROD_SLA_SLG: 'd',
          videoLink: 'd',
          referensi_product: 'd',
          contacts: 'd',
          is_bookmarked: 'd',
          proposal_product: 'd',
          photo_product: 'd',
          photoProductLink: 'd',
          bannersLink: 'd',
          banner_web: 'd',
          child_details: 'd',
          CHILD: 'd',
          children: 'd',
          childs: 'd',
        },
      }

      this.$http
        .post(
          `/product/browse/id/${this.$route.params.key}/set/first`,
          mapRequest
        )
        .then(response => {
          const records = response?.data?.data?.records || {}
          this.$store.commit('app/SET_PAGE_TITLE', records.PRODUCT)
          this.descTitle = records.PRODUCT
          this.lastUpdate = records.LAST_UPDATE
          this.desc = records.PROD_DESCRIPTION
          this.benefit =
            records.PROD_BENEFIT == null || records.PROD_BENEFIT == ''
              ? '-'
              : records.PROD_BENEFIT
          if (this.benefit == '-') {
            this.proposalOptions = this.proposalOptions.filter(
              item => item.val !== 'benefit'
            )
          }
          this.valueProposition =
            records.VALUE_PROPOSITION == null || records.VALUE_PROPOSITION == ''
              ? '-'
              : records.VALUE_PROPOSITION
          this.salesProposition =
            records.SALES_PROPOSITION == null || records.SALES_PROPOSITION == ''
              ? '-'
              : records.SALES_PROPOSITION
          this.specificationFeatures =
            records.PROD_FEATURES == null || records.PROD_FEATURES == ''
              ? '-'
              : records.PROD_FEATURES
          if (this.specificationFeatures == '-') {
            this.proposalOptions = this.proposalOptions.filter(
              item => item.val !== 'fitur'
            )
          }
          this.stp =
            records.PROD_CUST_STP == null || records.PROD_CUST_STP == ''
              ? '-'
              : records.PROD_CUST_STP
          this.config =
            records.PROD_CONFIG == null || records.PROD_CONFIG == ''
              ? '-'
              : records.PROD_CONFIG
          if (this.config == '-') {
            this.proposalOptions = this.proposalOptions.filter(
              item => item.val !== 'konfigurasi'
            )
          }
          this.sla =
            records.PROD_SLA_SLG == null || records.PROD_SLA_SLG == ''
              ? '-'
              : records.PROD_SLA_SLG
          if (this.sla == '-') {
            this.proposalOptions = this.proposalOptions.filter(
              item => item.val !== 'slaslg'
            )
          }

          this.childProducts = this.extractChildProducts(records)
          this.setVideoAttachment(records?.videoLink)
          this.applyProductHeroImage(records)
          this.proposalFiles = this.parseAttachmentField(
            records.proposal_product
          )
          this.referensi_product = this.parseAttachmentField(
            records.referensi_product
          )

          if (Array.isArray(this.referensi_product)) {
            this.referensi_product.sort((a, b) => {
              return String(a?.usrName || '').localeCompare(
                String(b?.usrName || ''),
                'en',
                { numeric: true }
              )
            })
          }

          const contacts = Array.isArray(records.contacts)
            ? records.contacts
            : []
          this.contacts_pi = contacts.filter(
            item => item.type === 'product_information'
          )
          this.contacts_com = contacts.filter(
            item => item.type === 'commercial'
          )
          this.fetchTestimonials()
          this.applyBookmarkState(records.is_bookmarked)
          this.recordNavContext = this.resolveContextFromRecord(records)
          this.applyQuickLinks()
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          this.childProducts = []
          this.brokenChildThumbnails = {}
          this.testimonials = []
        })
    },
    getNodeChildren(node = {}) {
      if (Array.isArray(node.child_details) && node.child_details.length) {
        return node.child_details
      }
      if (Array.isArray(node.CHILD) && node.CHILD.length) {
        return node.CHILD
      }
      if (Array.isArray(node.children) && node.children.length) {
        return node.children
      }
      if (Array.isArray(node.childs) && node.childs.length) {
        return node.childs
      }
      return []
    },
    stripHtml(value) {
      if (!value) return ''
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    summarizeText(value, maxLength = 130) {
      const cleaned = this.stripHtml(value)
      if (!cleaned) return 'Deskripsi belum tersedia.'
      if (cleaned.length <= maxLength) return cleaned
      return `${cleaned.slice(0, maxLength).trim()}...`
    },
    resolveChildThumbnail(node = {}) {
      const parsedFromAttachments = this.parseAttachmentField(
        node.photoProductLink ||
          node.productPhotoLink ||
          node.photo_product ||
          node.PHOTO_PRODUCT
      )

      const attachmentPath =
        parsedFromAttachments?.[0]?.link ||
        parsedFromAttachments?.[0]?.file_url ||
        parsedFromAttachments?.[0]?.path ||
        parsedFromAttachments?.[0]?.name ||
        ''

      const directThumbnail =
        node.photo ||
        node.PHOTO ||
        node.image ||
        node.IMAGE ||
        node.thumbnail ||
        node.THUMBNAIL ||
        ''

      return this.resolveFileUrl(attachmentPath || directThumbnail) || ''
    },
    normalizeChildProduct(node = {}, depth = 0) {
      const code =
        node.KODE || node.PRODUCT_CODE || node.product_code || node.code || ''
      const name =
        node.PRODUCT ||
        node.product ||
        node.name ||
        node.PRODUCT_NAME ||
        'Unnamed Product'
      const category =
        node?.product_category_details?.name ||
        node?.product_category_details?.id ||
        node.PROD_CATEGORY ||
        node.prod_category ||
        ''
      const summarySource =
        node.PROD_DESCRIPTION ||
        node.description ||
        node.DESC ||
        node.desc ||
        node.PROD_BENEFIT ||
        node.SALES_PROPOSITION ||
        ''
      const description = this.summarizeText(summarySource, 180)
      return {
        key: `${code || name}-${depth}`,
        code: String(code || '').trim(),
        name: String(name || '').trim(),
        category: String(category || '').trim(),
        description,
        thumbnail: this.resolveChildThumbnail(node),
      }
    },
    extractChildProducts(records = {}) {
      const rootChildren = this.getNodeChildren(records)
      const flattened = []
      const walk = (nodes = [], depth = 0) => {
        nodes.forEach(node => {
          const normalized = this.normalizeChildProduct(node, depth)
          if (normalized.code && normalized.code !== this.$route.params.key) {
            flattened.push(normalized)
          }
          const nested = this.getNodeChildren(node)
          if (nested.length) {
            walk(nested, depth + 1)
          }
        })
      }
      walk(rootChildren, 0)
      return flattened.filter(
        (item, idx, arr) =>
          item.code && arr.findIndex(x => x.code === item.code) === idx
      )
    },
    openChildProduct(child) {
      if (!child?.code) return
      this.$router.push({
        name: this.$route.name || 'product-detail',
        params: { ...this.$route.params, key: child.code },
      })
    },
    hasChildThumbnail(child = {}) {
      if (!child?.thumbnail) return false
      return !this.brokenChildThumbnails[child.key]
    },
    onChildThumbnailError(child = {}) {
      if (!child?.key) return
      this.$set(this.brokenChildThumbnails, child.key, true)
    },
    getRouteContext() {
      const routeName = this.$route?.name || ''
      const rootText = this.$route?.meta?.breadcrumb?.[0]?.text || ''
      const breadcrumbContext = this.$route?.meta?.breadcrumb?.[1]?.text || ''
      if (routeName.includes('bundling') || rootText === 'Product Bundling') {
        return 'Bundling'
      }
      const knownContexts = [
        'Communication',
        'Datacomm',
        'Internet',
        'Wireless',
        'CONS',
        'WIBS',
        'EBIS',
        'Segmentation',
        'Targeting',
        'Cyber Security',
        'Fitur',
        'Produk',
        'Varian',
        'DTP',
      ]
      return knownContexts.includes(breadcrumbContext) ? breadcrumbContext : ''
    },
    resolveContextFromRecord(records = {}) {
      const raw =
        records?.product_category_details?.name ??
        records?.PROD_CATEGORY ??
        records?.prod_category
      if (raw === null || raw === undefined) return ''
      const normalized = String(raw)
        .trim()
        .toLowerCase()
      if (!normalized) return ''
      if (normalized === 'cons') return 'CONS'
      if (normalized === 'wibs') return 'WIBS'
      if (normalized === 'ebis') return 'EBIS'
      if (normalized === 'segmentation') return 'Segmentation'
      if (normalized === 'targeting') return 'Targeting'
      if (normalized === 'bundling' || normalized === 'product bundling')
        return 'Bundling'
      if (normalized === 'fitur') return 'Fitur'
      if (normalized === 'produk') return 'Produk'
      if (normalized === 'varian') return 'Varian'
      if (
        normalized === 'dtp' ||
        normalized === 'digital touch point'
      )
        return 'DTP'
      if (normalized === '1' || normalized.includes('communication'))
        return 'Communication'
      if (normalized === '2' || normalized.includes('datacomm'))
        return 'Datacomm'
      if (normalized === '3' || normalized.includes('internet'))
        return 'Internet'
      if (normalized === '4' || normalized.includes('wireless'))
        return 'Wireless'
      if (normalized === '5' || normalized.includes('cyber'))
        return 'Cyber Security'
      return ''
    },
    getNavigationContext() {
      return this.getRouteContext() || this.recordNavContext || ''
    },
    getNavigationTargets(context = this.getNavigationContext()) {
      const map = {
        Communication: {
          faq: 'product-communication-faq',
          tarif: 'product-communication-tarif',
          compare: 'communication-compare-product',
          case: 'product-communication-casestudy',
        },
        Datacomm: {
          faq: 'product-datacomm-faq',
          tarif: 'product-datacomm-tarif',
          compare: 'datacomm-compare-product',
          case: 'product-datacomm-casestudy',
        },
        Internet: {
          faq: 'product-internet-faq',
          tarif: 'product-internet-tarif',
          compare: 'internet-compare-product',
          case: 'product-internet-casestudy',
        },
        Wireless: {
          faq: 'product-wireless-faq',
          tarif: 'product-wireless-tarif',
          compare: 'wireless-compare-product',
          case: 'product-wireless-casestudy',
        },
        CONS: {
          faq: 'product-cfu-cons-faq',
          tarif: 'product-cons-tarif',
          compare: 'cons-compare-product',
          case: 'product-cons-casestudy',
        },
        WIBS: {
          faq: 'product-cfu-wibs-faq',
          tarif: 'product-wibs-tarif',
          compare: 'wibs-compare-product',
          case: 'product-wibs-casestudy',
        },
        EBIS: {
          faq: 'product-cfu-ebis-faq',
          tarif: 'product-ebis-tarif',
          compare: 'ebis-compare-product',
          case: 'product-ebis-casestudy',
        },
        Segmentation: {
          faq: 'product-stp-segmen-faq',
          tarif: 'product-segmen-tarif',
          compare: 'segmen-compare-product',
          case: 'product-segmen-casestudy',
        },
        Targeting: {
          faq: 'product-stp-target-faq',
          tarif: 'product-target-tarif',
          compare: 'target-compare-product',
          case: 'product-target-casestudy',
        },
        'Cyber Security': {
          faq: 'product-cyber-security-faq',
          tarif: 'product-cyber-security-tarif',
          compare: 'cyber-security-compare-product',
          case: 'product-cyber-security-casestudy',
        },
        Bundling: {
          faq: 'product-bundling-faq',
          tarif: 'product-bundling-tarif',
          compare: 'bundling-compare-product',
          case: 'product-bundling-casestudy',
        },
        Fitur: {
          faq: 'product-fitur-faq',
          tarif: 'product-fitur-tarif',
          compare: 'fitur-compare-product',
          case: 'product-fitur-casestudy',
        },
        Produk: {
          faq: 'product-produk-faq',
          tarif: 'product-produk-tarif',
          compare: 'produk-compare-product',
          case: 'product-produk-casestudy',
        },
        Varian: {
          faq: 'product-varian-faq',
          tarif: 'product-varian-tarif',
          compare: 'varian-compare-product',
          case: 'product-varian-casestudy',
        },
        DTP: {
          faq: 'product-dtp-faq',
          tarif: 'product-dtp-tarif',
          compare: 'dtp-compare-product',
          case: 'product-dtp-casestudy',
        },
      }
      return map[context] || {}
    },
    applyQuickLinks() {
      const targets = this.getNavigationTargets()
      const { key } = this.$route.params
      const toHref = routeName =>
        routeName
          ? this.$router.resolve({ name: routeName, params: { key } }).href
          : ''
      this.faqHref = toHref(targets.faq)
      this.tarifHref = toHref(targets.tarif)
      this.compareHref = toHref(targets.compare)
      this.caseHref = toHref(targets.case)
    },
    navigateToTarget(targetKey) {
      const targets = this.getNavigationTargets()
      const routeName = targets[targetKey]
      if (!routeName) return
      this.$router.push({
        name: routeName,
        params: { key: this.$route.params.key },
      })
    },
    parseAttachmentField(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) return raw.filter(Boolean)
      if (typeof raw === 'object') return [raw]
      if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (!trimmed) return []
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) return parsed.filter(Boolean)
          if (parsed && typeof parsed === 'object') return [parsed]
          return []
        } catch (e) {
          return [{ link: trimmed, name: trimmed, usrName: trimmed }]
        }
      }
      return []
    },
    getAttachmentPrimaryPath(item) {
      return item?.link || item?.file_url || item?.path || item?.name || ''
    },
    setVideoAttachment(raw) {
      const [item] = this.parseAttachmentField(raw)
      this.videoItem = item || null
      this.video = this.resolveFileUrl(this.getAttachmentPrimaryPath(item))
    },
    applyProductHeroImage(records = {}) {
      const bannerWebPhotos = this.parseAttachmentField(
        records.bannersLink || records.banner_web
      )
      const fallbackPhotos = this.parseAttachmentField(
        records.photoProductLink ||
          records.productPhotoLink ||
          records.photo_product
      )
      const prioritizedPhotos = bannerWebPhotos.length
        ? bannerWebPhotos
        : fallbackPhotos

      this.productPhotos = prioritizedPhotos

      const mainPhoto = this.buildFileUrl(prioritizedPhotos?.[0]?.link)
      this.productImage = mainPhoto || this.defaultProductImage
    },
    buildFileUrl(path) {
      if (!path) return ''
      const urlCandidate = String(path)
      if (/^https?:\/\//i.test(urlCandidate)) {
        return urlCandidate
      }
      const cleanPath = urlCandidate.replace(/^\/+/, '')
      const base = process.env.VUE_APP_API_HOST || ''
      const separator = base.endsWith('/') ? '' : '/'
      const normalizedPath = cleanPath.startsWith('storage/')
        ? cleanPath
        : `storage/${cleanPath}`
      return `${base}${separator}${normalizedPath}`
    },
    resolveFileUrl(path) {
      if (!path) return ''
      const candidate = String(path)
      if (/^https?:\/\//i.test(candidate)) {
        return candidate
      }
      return this.buildFileUrl(candidate)
    },
    openVideoPreview() {
      if (!this.hasVideo) return
      const item = this.videoItem || { link: this.video, name: this.video }
      const filename = this.buildDownloadFilename(item, 'product-video')
      const rawPath = this.getAttachmentPrimaryPath(item) || this.video
      const url = this.resolveFileUrl(rawPath)

      this.videoPreviewRequestId += 1
      this.videoPreviewRetried = false
      this.clearVideoPreview()
      this.videoPreview.loading = false
      this.videoPreview.error = ''
      this.videoPreview.url = url
      this.videoPreview.mime = item?.type || 'video/mp4'
      this.videoPreview.filename = filename
      this.$refs['modal-video-preview']?.show()
    },
    setVideoPreviewError(message) {
      this.videoPreview.loading = false
      this.videoPreview.error = message || 'Gagal memuat video'
    },
    async onVideoPreviewError() {
      const requestId = this.videoPreviewRequestId
      if (this.videoPreviewRetried) {
        this.setVideoPreviewError('Gagal memuat video')
        return
      }

      this.videoPreviewRetried = true
      try {
        await this.refreshProductAttachments()
        if (requestId !== this.videoPreviewRequestId) return

        const item = this.videoItem || { link: this.video, name: this.video }
        const refreshedUrl = this.resolveFileUrl(
          this.getAttachmentPrimaryPath(item) || this.video
        )

        if (!refreshedUrl) {
          this.setVideoPreviewError('Link video tidak ditemukan')
          return
        }

        this.videoPreview.error = ''
        this.videoPreview.url = refreshedUrl

        this.$nextTick(() => {
          const player = this.$refs.videoPreviewPlayer
          if (player && typeof player.load === 'function') {
            player.load()
          }
        })
      } catch (error) {
        this.setVideoPreviewError('Gagal memuat video')
      }
    },
    stopVideoPreview() {
      const player = this.$refs.videoPreviewPlayer
      if (player && typeof player.pause === 'function') {
        try {
          player.pause()
          player.currentTime = 0
        } catch (e) {
          // ignore
        }
      }
    },
    clearVideoPreview() {
      if (
        this.videoPreview?.url &&
        String(this.videoPreview.url).startsWith('blob:')
      ) {
        try {
          window.URL.revokeObjectURL(this.videoPreview.url)
        } catch (e) {
          // ignore
        }
      }
      this.videoPreview = {
        loading: false,
        error: '',
        url: '',
        blob: null,
        filename: '',
        mime: '',
      }
    },
    handleVideoPreviewHidden() {
      this.videoPreviewRequestId += 1
      this.videoPreviewRetried = false
      this.stopVideoPreview()
      this.clearVideoPreview()
    },
    getVideoProductFilename(item = {}) {
      return this.buildDownloadFilename(
        {
          ...item,
          usrName: this.descTitle || 'product-video',
        },
        this.descTitle || 'product-video'
      )
    },
    saveBlobAs(blob, filename) {
      if (!blob) return
      const downloadName = this.normalizeFilename(filename, 'document')
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadName
      link.rel = 'noopener'
      link.click()
      window.URL.revokeObjectURL(url)
    },
    async downloadVideoPreview() {
      if (!this.hasVideo) return

      const loadingId = 'video-download'
      const item = {
        ...(this.videoItem || {}),
        type: this.videoPreview?.mime || this.videoItem?.type,
      }
      const filename = this.getVideoProductFilename(item)
      const rawPath = this.getAttachmentPrimaryPath(item) || this.video
      const url = this.resolveFileUrl(rawPath)

      const firstTry = await this.fetchFileBlob(
        url,
        'videoLoading',
        loadingId,
        {
          silent: true,
        }
      )

      if (firstTry?.ok) {
        this.saveBlobAs(firstTry.blob, filename)
        return
      }

      if (
        this.shouldRetryExpiredDownload(firstTry?.status, firstTry?.message)
      ) {
        try {
          await this.refreshProductAttachments()
          const refreshedItem = {
            ...(this.videoItem || item),
            type: this.videoItem?.type || item?.type || this.videoPreview?.mime,
          }
          const refreshedFilename = this.getVideoProductFilename(refreshedItem)
          const refreshedPath =
            this.getAttachmentPrimaryPath(refreshedItem) || this.video
          const refreshedUrl = this.resolveFileUrl(refreshedPath)

          const secondTry = await this.fetchFileBlob(
            refreshedUrl,
            'videoLoading',
            loadingId,
            { silent: true }
          )
          if (secondTry?.ok) {
            this.saveBlobAs(secondTry.blob, refreshedFilename)
            return
          }

          const message =
            secondTry?.message || firstTry?.message || 'Gagal mengunduh video'
          this.$bvToast.toast(message, {
            variant: 'danger',
            solid: true,
          })
          return
        } catch (e) {
          // fall through to default message
        }
      }

      const message = firstTry?.message || 'Gagal mengunduh video'
      this.$bvToast.toast(message, {
        variant: 'danger',
        solid: true,
      })
    },
    normalizeFilename(value, fallbackName = 'document') {
      if (!value) return fallbackName
      const trimmed = String(value).trim()
      if (!trimmed) return fallbackName
      const noQuery = trimmed.split('?')[0]
      const lastSegment = noQuery
        .split('/')
        .filter(Boolean)
        .pop()
      const base = lastSegment || noQuery || fallbackName
      return base.replace(/[\\/:*?"<>|]+/g, '_')
    },
    isAttachmentPathLike(value) {
      if (!value) return false
      const text = String(value).trim()
      if (!text) return false
      if (/^https?:\/\//i.test(text)) return true
      if (text.includes('/') || text.includes('\\')) return true
      if (text.includes('?') && text.includes('=')) return true
      return false
    },
    extractAttachmentDisplayName(value) {
      if (!value) return ''
      const raw = String(value).trim()
      if (!raw) return ''
      const noQuery = raw.split('?')[0]
      const lastSegment =
        noQuery
          .split('/')
          .filter(Boolean)
          .pop() || noQuery
      let decoded = lastSegment
      try {
        decoded = decodeURIComponent(lastSegment)
      } catch (e) {
        // ignore malformed uri component
      }
      return decoded.replace(/\.[a-z0-9]{2,6}$/i, '').trim()
    },
    inferPreviewKind(mime, filename) {
      const type = String(mime || '').toLowerCase()
      if (type.includes('pdf')) return 'pdf'
      if (type.startsWith('image/')) return 'image'
      if (
        type.includes(
          'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        )
      ) {
        return 'pptx'
      }
      if (
        type.includes(
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        )
      ) {
        return 'docx'
      }
      if (type.includes('msword') || type.includes('ms-powerpoint')) {
        return 'office'
      }

      const ext = String(filename || '')
        .toLowerCase()
        .split('?')[0]
        .split('.')
        .pop()

      if (ext === 'pdf') return 'pdf'
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
        return 'image'
      }
      return 'file'
    },
    buildOfficePreviewUrl(sourceUrl) {
      const raw = String(sourceUrl || '').trim()
      if (!/^https?:\/\//i.test(raw)) return ''
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        raw
      )}`
    },
    getFileExtensionFromPath(value) {
      if (!value) return ''
      const noQuery = String(value).split('?')[0]
      const lastSegment =
        noQuery
          .split('/')
          .filter(Boolean)
          .pop() || ''
      const parts = lastSegment.split('.')
      if (parts.length < 2) return ''
      const ext = parts.pop()
      return ext ? String(ext).toLowerCase() : ''
    },
    getFileExtensionFromMime(mime) {
      const type = String(mime || '').toLowerCase()
      if (!type) return ''

      const known = {
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          'xlsx',
        'application/vnd.ms-powerpoint': 'ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          'pptx',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/bmp': 'bmp',
        'image/svg+xml': 'svg',
        'video/mp4': 'mp4',
        'video/quicktime': 'mov',
        'application/zip': 'zip',
        'application/x-zip-compressed': 'zip',
      }

      if (known[type]) return known[type]

      if (type.startsWith('image/')) {
        const imageExt = type.split('/')[1]
        if (imageExt === 'jpeg') return 'jpg'
        if (imageExt === 'svg+xml') return 'svg'
        return imageExt || ''
      }

      if (type.startsWith('video/')) {
        const videoExt = type.split('/')[1]
        return videoExt || ''
      }

      return ''
    },
    ensureFilenameExtension(baseName, extension) {
      const base = this.normalizeFilename(baseName, 'document')
      const ext = String(extension || '')
        .toLowerCase()
        .replace(/^\./, '')
      if (!ext) return base

      const dotExt = `.${ext}`
      const lower = base.toLowerCase()

      const knownExtensions = [
        '.pdf',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.ppt',
        '.pptx',
        '.png',
        '.jpg',
        '.jpeg',
        '.gif',
        '.webp',
        '.bmp',
        '.svg',
        '.mp4',
        '.mov',
        '.avi',
        '.mkv',
        '.zip',
        '.rar',
        '.7z',
      ]

      const existing = knownExtensions.find(candidate =>
        lower.endsWith(candidate)
      )
      if (existing) {
        if (existing === dotExt) return base
        return `${base.slice(0, -existing.length)}${dotExt}`
      }

      return `${base}${dotExt}`
    },
    buildDownloadFilename(item, fallbackBaseName = 'document') {
      const baseName = item?.usrName || fallbackBaseName
      const extFromMime = this.getFileExtensionFromMime(item?.type)
      const extFromPath =
        this.getFileExtensionFromPath(item?.name) ||
        this.getFileExtensionFromPath(item?.link)
      const extension = extFromMime || extFromPath
      return this.ensureFilenameExtension(baseName, extension)
    },
    startPreview({ title, filename } = {}) {
      this.clearPreview()
      this.preview.loading = true
      this.preview.error = ''
      this.preview.title = title || 'Document Preview'
      this.preview.filename = filename || 'document'
      this.$refs['modal-document-preview']?.show()
    },
    async resolvePptxPreviewInit() {
      if (typeof this.pptxPreviewInit === 'function') {
        return this.pptxPreviewInit
      }

      const module = await import('pptx-preview/dist/pptx-preview.es.js')
      const init = module?.init
      if (typeof init !== 'function') {
        throw new Error('pptx-preview init function is unavailable')
      }
      this.pptxPreviewInit = init
      return init
    },
    async resolveDocxRenderAsync() {
      if (typeof this.docxRenderAsync === 'function') {
        return this.docxRenderAsync
      }

      const module = await import('docx-preview/dist/docx-preview.js')
      const renderAsync = module?.renderAsync
      if (typeof renderAsync !== 'function') {
        throw new Error('docx-preview renderAsync is unavailable')
      }
      this.docxRenderAsync = renderAsync
      return renderAsync
    },
    destroyPptxPreviewer() {
      if (
        this.pptxPreviewer &&
        typeof this.pptxPreviewer.destroy === 'function'
      ) {
        this.pptxPreviewer.destroy()
      }
      this.pptxPreviewer = null

      const container = this.$refs.pptxPreviewContainer
      if (container) {
        container.innerHTML = ''
      }
    },
    clearDocxPreviewContainer() {
      const container = this.$refs.docxPreviewContainer
      if (container) {
        container.innerHTML = ''
      }
    },
    clearStructuredPreviewContainers() {
      this.destroyPptxPreviewer()
      this.clearDocxPreviewContainer()
    },
    async blobToArrayBuffer(blob) {
      if (!blob) return null
      if (typeof blob.arrayBuffer === 'function') {
        return blob.arrayBuffer()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsArrayBuffer(blob)
      })
    },
    async renderPptxPreview(blob, sourceUrl, requestId) {
      if (!blob || requestId !== this.previewRequestId) return
      try {
        const init = await this.resolvePptxPreviewInit()
        if (requestId !== this.previewRequestId) return

        await this.$nextTick()
        if (requestId !== this.previewRequestId) return

        const container = this.$refs.pptxPreviewContainer
        if (!container) {
          throw new Error('PPTX preview container not found')
        }

        this.destroyPptxPreviewer()
        const arrayBuffer = await this.blobToArrayBuffer(blob)
        if (!arrayBuffer || requestId !== this.previewRequestId) return

        const width = container.clientWidth > 0 ? container.clientWidth : 960
        const previewer = init(container, {
          width,
          height: 540,
          mode: 'list',
        })
        this.pptxPreviewer = previewer
        await previewer.preview(arrayBuffer)
      } catch (error) {
        console.error('[DocumentPreview] pptx-preview render failed', error)
        const fallbackOfficeUrl = this.buildOfficePreviewUrl(sourceUrl)
        if (fallbackOfficeUrl && requestId === this.previewRequestId) {
          this.preview.kind = 'office'
          this.preview.officeUrl = fallbackOfficeUrl
          this.preview.error = ''
          return
        }
        if (requestId === this.previewRequestId) {
          this.setPreviewError('Gagal merender file PPTX')
        }
      }
    },
    async renderDocxPreview(blob, sourceUrl, requestId) {
      if (!blob || requestId !== this.previewRequestId) return
      try {
        const renderAsync = await this.resolveDocxRenderAsync()
        if (requestId !== this.previewRequestId) return

        await this.$nextTick()
        if (requestId !== this.previewRequestId) return

        const container = this.$refs.docxPreviewContainer
        if (!container) {
          throw new Error('DOCX preview container not found')
        }

        this.clearStructuredPreviewContainers()
        const arrayBuffer = await this.blobToArrayBuffer(blob)
        if (!arrayBuffer || requestId !== this.previewRequestId) return

        await renderAsync(arrayBuffer, container, null, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
        })
      } catch (error) {
        console.error('[DocumentPreview] docx-preview render failed', error)
        const fallbackOfficeUrl = this.buildOfficePreviewUrl(sourceUrl)
        if (fallbackOfficeUrl && requestId === this.previewRequestId) {
          this.preview.kind = 'office'
          this.preview.officeUrl = fallbackOfficeUrl
          this.preview.error = ''
          return
        }
        if (requestId === this.previewRequestId) {
          this.setPreviewError('Gagal merender file DOCX')
        }
      }
    },
    setPreviewFromBlob(blob, { mime, filename, sourceUrl } = {}) {
      if (!blob) return
      const resolvedMime = mime || blob.type || ''
      const safeFilename = this.normalizeFilename(
        filename || this.preview.filename || 'document',
        'document'
      )

      if (this.preview.url) {
        try {
          window.URL.revokeObjectURL(this.preview.url)
        } catch (e) {
          // ignore
        }
      }

      this.preview.blob = blob
      this.preview.mime = resolvedMime
      this.preview.filename = safeFilename
      this.preview.kind = this.inferPreviewKind(resolvedMime, safeFilename)
      this.preview.sourceUrl = sourceUrl || ''
      this.preview.officeUrl =
        this.preview.kind === 'office'
          ? this.buildOfficePreviewUrl(sourceUrl)
          : ''
      this.preview.url = window.URL.createObjectURL(blob)
      this.preview.loading = false
      this.preview.error = ''

      if (this.preview.kind === 'pptx') {
        const requestId = this.previewRequestId
        this.renderPptxPreview(blob, sourceUrl, requestId)
      } else if (this.preview.kind === 'docx') {
        const requestId = this.previewRequestId
        this.renderDocxPreview(blob, sourceUrl, requestId)
      } else {
        this.clearStructuredPreviewContainers()
      }
    },
    setPreviewError(message) {
      this.preview.loading = false
      this.preview.error = message || 'Gagal memuat dokumen'
    },
    clearPreview() {
      if (this.preview?.url) {
        try {
          window.URL.revokeObjectURL(this.preview.url)
        } catch (e) {
          // ignore
        }
      }
      this.preview = {
        loading: false,
        error: '',
        url: '',
        blob: null,
        title: '',
        filename: '',
        kind: '',
        mime: '',
      }
    },
    downloadPreview() {
      if (!this.preview?.blob) return
      const filename = this.normalizeFilename(
        this.preview.filename || 'document'
      )
      const url = window.URL.createObjectURL(this.preview.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.rel = 'noopener'
      link.click()
      window.URL.revokeObjectURL(url)
    },
    async readBlobText(blob) {
      if (!blob) return ''
      if (typeof blob.text === 'function') {
        return blob.text()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result || '')
        reader.onerror = reject
        reader.readAsText(blob)
      })
    },
    async getErrorMessage(error) {
      const fallback =
        error?.message || error?.statusText || 'Gagal membuka dokumen'

      const data = error?.data ?? error?.response?.data
      if (!data) return fallback

      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data)
          if (parsed?.message) return parsed.message
        } catch (e) {
          // ignore
        }
        return data || fallback
      }

      if (data instanceof Blob) {
        try {
          const text = await this.readBlobText(data)
          if (!text) return fallback
          try {
            const parsed = JSON.parse(text)
            if (parsed?.message) return parsed.message
          } catch (e) {
            // ignore
          }
          return text
        } catch (e) {
          return fallback
        }
      }

      if (typeof data === 'object' && data?.message) {
        return data.message
      }

      return fallback
    },
    shouldRetryExpiredDownload(status, message) {
      if (Number(status) !== 403) return false
      const text = String(message || '').toLowerCase()
      return (
        text.includes('went idle') ||
        text.includes('idle for too long') ||
        text.includes('refresh the product detail')
      )
    },
    async refreshProductAttachments() {
      const mapRequest = {
        map: {
          referensi_product: 'd',
          proposal_product: 'd',
          photo_product: 'd',
          photoProductLink: 'd',
          bannersLink: 'd',
          banner_web: 'd',
          videoLink: 'd',
        },
      }

      const response = await this.$http.post(
        `/product/browse/id/${this.$route.params.key}/set/first`,
        mapRequest,
        { skipGlobalErrorNotif: true }
      )

      const records = response?.data?.data?.records || {}

      const references = this.parseAttachmentField(records.referensi_product)
      if (Array.isArray(references)) {
        references.sort((a, b) =>
          String(a?.usrName || '').localeCompare(
            String(b?.usrName || ''),
            'en',
            { numeric: true }
          )
        )
      }
      this.referensi_product = references

      this.proposalFiles = this.parseAttachmentField(records.proposal_product)

      this.applyProductHeroImage(records)

      this.setVideoAttachment(records.videoLink)

      return records
    },
    async fetchFileBlob(url, loadingKey, loadingId, { silent = false } = {}) {
      if (!url) {
        const message = 'Link dokumen tidak ditemukan'
        if (!silent) {
          this.$bvToast.toast(message, {
            variant: 'warning',
            solid: true,
          })
        }
        return { ok: false, status: 0, message, blob: null, mime: '' }
      }

      if (!this[loadingKey]) {
        this.$set(this, loadingKey, [])
      }

      this[loadingKey].push(loadingId)

      try {
        const response = await this.$http.get(url, {
          responseType: 'blob',
          skipGlobalErrorNotif: true,
        })
        const contentType = response?.headers?.['content-type'] || ''
        const blob =
          response?.data instanceof Blob
            ? response.data.type
              ? response.data
              : new Blob([response.data], { type: contentType })
            : new Blob([response.data], { type: contentType })
        return { ok: true, blob, mime: blob?.type || contentType }
      } catch (error) {
        const status = error?.status || error?.response?.status || 0
        const message = await this.getErrorMessage(error)
        if (!silent) {
          this.$bvToast.toast(message, {
            variant: 'danger',
            solid: true,
          })
        }
        return { ok: false, status, message, blob: null, mime: '' }
      } finally {
        this[loadingKey] = (this[loadingKey] || []).filter(
          id => id !== loadingId
        )
      }
    },
    proposalIcon(type) {
      const mime = (type || '').toLowerCase()
      if (mime.includes('pdf')) return 'FileTextIcon'
      if (mime.includes('sheet') || mime.includes('excel')) return 'GridIcon'
      if (mime.includes('word') || mime.includes('doc')) return 'FileIcon'
      if (mime.includes('image')) return 'ImageIcon'
      return 'PaperclipIcon'
    },
    proposalType(type) {
      if (!type) return 'FILE'
      const mime = type.toLowerCase()
      if (mime.includes('pdf')) return 'PDF'
      if (mime.includes('sheet') || mime.includes('excel')) return 'XLSX'
      if (mime.includes('word') || mime.includes('doc')) return 'DOCX'
      if (mime.includes('image')) return 'IMG'
      return 'FILE'
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 MB'
      if (!bytes) return '-'
      return `${this.toMB(bytes)} MB`
    },
    toMB(bytes) {
      const mb = bytes * 0.000001
      return mb.toFixed(2)
    },
    waNumber(num) {
      if (num.substring(0, 2) == '08') {
        return `+62${num.slice(1)}`
      }
      if (num.substring(0, 1) == '8') {
        return `+62${num}`
      }

      return num
    },
    faq() {
      this.navigateToTarget('faq')
    },
    compareProduct() {
      this.navigateToTarget('compare')
    },
    tarif() {
      if (!this.canTarif) {
        this.showModalNeedAccess()
      } else {
        this.navigateToTarget('tarif')
      }
    },
    // notadinas() {
    //   if(this.$route.meta.breadcrumb[1].text == 'Datacomm'){
    //     this.$router.push({name : 'product-datacomm-notadinas', params:{key:this.$route.params.key}})
    //   }else{
    //     this.$router.push({})
    //   }
    // },
    casestudy() {
      this.navigateToTarget('case')
    },
    proposal() {
      this.$router.push({
        name: 'product-proposal-index',
        params: { id: this.$route.params.key },
        query: { from: this.$route.name },
      })
    },
    onScroll() {
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop
      if (currentScrollPosition < 0) {
        return
      }
      // Stop executing this function if the difference between
      // current scroll position and last scroll position is less than some offset
      if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 60) {
        return
      }

      this.showNavbar = currentScrollPosition < 120
      this.lastScrollPosition = currentScrollPosition
    },
    showModalNeedAccess() {
      this.$refs['modal-request-access'].show()
    },
    async requestAccess() {
      this.$refs['modal-request-access'].hide()
      this.setAlreadyRequest()
      this.$notif('Berhasil', 'Request akses berhasil!', 'success')
      return await this.$http
        .post('/user/request-access')
        .then(res => {})
        .catch(err => {
          this.$notif('Error', 'Request access error!', 'danger')
          console.error()
        })
    },
    setAlreadyRequest() {
      localStorage.setItem(
        `request:${btoa(this.$store.getters['oauth/getActiveUser'].email)}`,
        1
      )
      this.alreadyRequest = 1
    },
    isAlreadyRequest() {
      const already = localStorage.getItem(
        `request:${btoa(this.$store.getters['oauth/getActiveUser'].email)}`
      )
      if (already) {
        return true
      }
      return false
    },
    async openReferenceLink(index) {
      const item = this.referensi_product?.[index]
      if (!item) return

      const fileKey = item?.name || item?.usrName || String(index)
      const filename = this.buildDownloadFilename(item, 'document')
      const title = item?.usrName || filename
      const rawPath = item?.link || item?.file_url || item?.path || item?.name
      const url = this.resolveFileUrl(rawPath)

      this.startPreview({ title, filename })

      const firstTry = await this.fetchFileBlob(
        url,
        'referencesLoading',
        index,
        {
          silent: true,
        }
      )
      if (firstTry?.ok) {
        this.setPreviewFromBlob(firstTry.blob, {
          mime: item?.type || firstTry.mime,
          filename,
        })
        return
      }

      if (
        this.shouldRetryExpiredDownload(firstTry?.status, firstTry?.message)
      ) {
        try {
          await this.refreshProductAttachments()
          const refreshedItem =
            (this.referensi_product || []).find(
              x =>
                x?.name === item?.name ||
                x?.usrName === item?.usrName ||
                x?.name === fileKey ||
                x?.usrName === fileKey
            ) || this.referensi_product?.[index]
          const refreshedPath =
            refreshedItem?.link ||
            refreshedItem?.file_url ||
            refreshedItem?.path ||
            refreshedItem?.name
          const refreshedUrl = this.resolveFileUrl(refreshedPath)

          const secondTry = await this.fetchFileBlob(
            refreshedUrl,
            'referencesLoading',
            index,
            { silent: true }
          )
          if (secondTry?.ok) {
            this.setPreviewFromBlob(secondTry.blob, {
              mime: refreshedItem?.type || item?.type || secondTry.mime,
              filename,
            })
            return
          }

          const message =
            secondTry?.message || firstTry?.message || 'Gagal membuka dokumen'
          this.setPreviewError(message)
          this.$bvToast.toast(message, {
            variant: 'danger',
            solid: true,
          })
          return
        } catch (e) {
          // fall through to default message
        }
      }

      const message = firstTry?.message || 'Gagal membuka dokumen'
      this.setPreviewError(message)
      this.$bvToast.toast(message, {
        variant: 'danger',
        solid: true,
      })
    },
    async openProposalAttachment(item) {
      const idx = this.proposalFiles.indexOf(item)
      const loadingId =
        idx === -1 ? item?.name || item?.usrName || 'proposal' : idx

      const fileKey = item?.name || item?.usrName || String(loadingId)
      const filename = this.buildDownloadFilename(item, 'proposal')
      const title = item?.usrName || filename
      const rawPath = item?.link || item?.name || item?.file_url || item?.path
      const url = this.resolveFileUrl(rawPath)

      this.startPreview({ title, filename })

      const firstTry = await this.fetchFileBlob(
        url,
        'proposalLoading',
        loadingId,
        {
          silent: true,
        }
      )
      if (firstTry?.ok) {
        this.setPreviewFromBlob(firstTry.blob, {
          mime: item?.type || firstTry.mime,
          filename,
        })
        return
      }

      if (
        this.shouldRetryExpiredDownload(firstTry?.status, firstTry?.message)
      ) {
        try {
          await this.refreshProductAttachments()
          const refreshedItem =
            (this.proposalFiles || []).find(
              x =>
                x?.name === item?.name ||
                x?.usrName === item?.usrName ||
                x?.name === fileKey ||
                x?.usrName === fileKey
            ) || item
          const refreshedPath =
            refreshedItem?.link ||
            refreshedItem?.name ||
            refreshedItem?.file_url ||
            refreshedItem?.path
          const refreshedUrl = this.resolveFileUrl(refreshedPath)

          const secondTry = await this.fetchFileBlob(
            refreshedUrl,
            'proposalLoading',
            loadingId,
            { silent: true }
          )
          if (secondTry?.ok) {
            this.setPreviewFromBlob(secondTry.blob, {
              mime: refreshedItem?.type || item?.type || secondTry.mime,
              filename,
            })
            return
          }

          const message =
            secondTry?.message || firstTry?.message || 'Gagal membuka dokumen'
          this.setPreviewError(message)
          this.$bvToast.toast(message, {
            variant: 'danger',
            solid: true,
          })
          return
        } catch (e) {
          // fall through to default message
        }
      }

      const message = firstTry?.message || 'Gagal membuka dokumen'
      this.setPreviewError(message)
      this.$bvToast.toast(message, {
        variant: 'danger',
        solid: true,
      })
    },
    getUserKode() {
      return this.$store.getters['oauth/getActiveUser']?.id || ''
    },
    persistBookmarkLocal(productKode, bookmarked) {
      try {
        const key = 'product_bookmarks'
        const raw = localStorage.getItem(key)
        let items = []
        if (raw) items = JSON.parse(raw)
        const kode = String(productKode)
        if (bookmarked) {
          if (!items.includes(kode)) items.push(kode)
        } else {
          items = items.filter(item => item !== kode)
        }
        localStorage.setItem(key, JSON.stringify(items))
      } catch (e) {
        // ignore persistence errors
      }
    },
    loadMoreDocuments() {
      this.documentVisibleCount = Math.min(
        this.referensi_product.length,
        this.documentVisibleCount + 5
      )
    },
    syncInitialBookmark() {
      if (this.backendBookmark !== null) {
        this.isBookmarked = this.backendBookmark
        return
      }
      try {
        const raw = localStorage.getItem('product_bookmarks')
        const kode = String(this.$route.params.key)
        const parsed = raw ? JSON.parse(raw) : []
        if (Array.isArray(parsed)) {
          this.isBookmarked = parsed.includes(kode)
        }
      } catch (e) {
        this.isBookmarked = false
      }
    },
    applyBookmarkState(flag) {
      if (flag === true || flag === false) {
        this.backendBookmark = flag
        this.isBookmarked = flag
      }
    },
    async toggleBookmark() {
      if (this.bookmarkLoading) return
      const userKode = this.getUserKode()
      const productKode = this.$route.params.key
      if (!userKode || !productKode) {
        this.$bvToast.toast('Data pengguna atau produk tidak ditemukan', {
          title: 'Bookmark',
          variant: 'danger',
          solid: true,
        })
        return
      }

      const willAdd = !this.isBookmarked
      const endpoint = willAdd
        ? `${process.env.VUE_APP_API_HELPER_URL}/product/bookmarks/add`
        : `${process.env.VUE_APP_API_HELPER_URL}/product/bookmarks/remove`

      this.bookmarkLoading = true
      try {
        await this.$http.put(endpoint, {
          user_kode: userKode,
          product_kode: [productKode],
        })
        this.isBookmarked = willAdd
        this.backendBookmark = willAdd
        this.persistBookmarkLocal(productKode, willAdd)
        this.$bvToast.toast(
          willAdd ? 'Successfull, Saved to bookmark' : 'Bookmark Deleted',
          {
            title: 'Bookmark',
            variant: willAdd ? 'success' : 'danger',
            solid: true,
          }
        )
      } catch (err) {
        this.$bvToast.toast('Gagal memperbarui bookmark', {
          title: 'Bookmark',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.bookmarkLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
.nav-vertical {
  padding-right: 0;
  overflow: hidden;
  border-right: 0.1px solid rgb(236, 236, 236);
  .nav.nav-tabs {
    // nav item styles for vertical nav tabs
    .nav-item {
      .nav-link {
        margin-bottom: 0;
        &:after {
          transform: rotate(90deg) translate3d(0, 150%, 0);
          left: 70%;
        }
        &.active {
          &:after {
            right: auto;
            left: -0.4rem;
            transform: rotate(90deg) translate3d(0, 225%, 0);
            top: 1.25rem;
            width: 2.14rem;
          }
        }
      }
    }
  }
}
</style>
<style scoped>
.topstick {
  margin-top: 3vh;
  margin-left: auto;
  width: fit-content;
}

.product-header {
  gap: 0.75rem;
}

.product-hero {
  position: relative;
  background: #0b1220;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.06);
}

.hero-media {
  position: relative;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.12) 40%,
    rgba(0, 0, 0, 0.55) 92%,
    rgba(0, 0, 0, 0.7) 100%
  );
  pointer-events: none;
}

.hero-footer {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
}

.product-hero .chip {
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #fff;
  backdrop-filter: blur(10px);
}

.product-hero .product-image,
.product-hero .product-image-placeholder {
  border-radius: 0;
}

.video-preview-player {
  max-height: 70vh;
  border-radius: 12px;
  background: #0b1220;
}

.media-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.04);
  border-radius: 14px;
  padding: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
}

.media-card.primary {
  background: radial-gradient(
      circle at 10% 10%,
      rgba(59, 130, 246, 0.06),
      transparent 45%
    ),
    #fff;
}

.media-card.secondary {
  background: radial-gradient(
      circle at 90% 0%,
      rgba(236, 72, 153, 0.06),
      transparent 40%
    ),
    #fff;
}

.media-label {
  font-weight: 700;
  font-size: 0.95rem;
  color: #0f172a;
}

.media-frame {
  position: relative;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  overflow: hidden;
  background: #0b1220;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  height: 320px;
}

.media-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0));
  pointer-events: none;
}

.media-overlay.subtle {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0));
}

.hero-video {
  display: block;
  height: 320px;
  object-fit: cover;
}

.doc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.doc-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  padding: 0.65rem 0.9rem;
  background: linear-gradient(90deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
}

.doc-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.doc-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(115, 103, 240, 0.12);
  color: #5e50ee;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.doc-title {
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
}

.doc-meta {
  font-size: 0.86rem;
  color: #6b7280;
}

.doc-actions {
  flex-shrink: 0;
}

.preview-frame {
  width: 100%;
  height: 75vh;
  border: 0;
  border-radius: 12px;
  background: #0b1220;
}

.preview-image {
  max-height: 75vh;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.pptx-preview-wrapper {
  height: 75vh;
  overflow: auto;
  border-radius: 12px;
  background: #f8fafc;
  padding: 0.75rem;
}

.pptx-preview-container {
  min-height: 100%;
}

.docx-preview-wrapper {
  height: 75vh;
  overflow: auto;
  border-radius: 12px;
  background: #f8fafc;
  padding: 0.75rem;
}

@media (max-width: 992px) {
  .docx-preview-wrapper {
    zoom: 0.4;
  }
}

.docx-preview-container {
  min-height: 100%;
}

@media (max-width: 576px) {
  .doc-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .doc-actions {
    width: 100%;
  }
}

.bookmark-btn {
  border-radius: 999px;
  font-weight: 600;
  padding: 0.35rem 1.4rem;
  box-shadow: 0 8px 18px rgba(22, 22, 22, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bookmark-btn:hover,
.bookmark-btn:focus {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(22, 22, 22, 0.15);
}

.bookmark-icon {
  font-size: 1.1rem;
  margin-right: 0.35rem;
}

.bookmark-label {
  font-size: 0.92rem;
}

.proposal-wrapper {
  border-radius: 1rem;
  border: none;
  box-shadow: 0 20px 50px rgba(18, 38, 63, 0.08);
}

.proposal-attachment {
  border: 1px solid rgba(90, 106, 133, 0.15);
  border-radius: 0.85rem;
  padding: 1rem;
  background: #fff;
  min-height: 180px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.proposal-attachment:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 35px rgba(18, 38, 63, 0.12);
}

.attachment-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.75rem;
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  font-size: 0.75rem;
  font-weight: 600;
}

.links-container {
  background-color: transparent;
}

@media (min-width: 992px) {
  .topstick {
    margin-left: unset;
  }
}

.b-avatar.badge-light-danger {
  background-color: red;
  color: white;
}

.rpIcon {
  font-size: 1em;
}

.txt-dark {
  color: black;
}

.bold {
  font-weight: 700;
}

.bold-sm {
  font-size: 0.8em;
  font-weight: 600;
}

.txt-md {
  color: black !important;
  font-size: 0.96em;
  font-weight: 600;
}

.txt-sm {
  color: black !important;
  font-size: 0.8em;
}

.txt-xs {
  font-size: 0.7em;
}

.txt-xs-2 {
  font-size: 0.9em;
}

.red {
  color: red;
}

.card-title {
  color: black !important;
}

body {
  color: black;
}

.ava-pink {
  color: red;
  background-color: rgb(252, 236, 236);
}

.product-image {
  border-radius: 0.75rem;
  object-fit: cover;
  height: 320px;
}

.hero-img {
  width: 100%;
  height: 320px;
}

.product-image-placeholder {
  width: 100%;
  border-radius: 0.75rem;
  height: 320px;
}

.sidebar-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 14px;
  padding: 1rem;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.link-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 0.55rem 0.25rem;
  border-radius: 10px;
  transition: background 0.15s ease;
  margin-bottom: 0.35rem;
}

.link-row:hover {
  background: rgba(115, 103, 240, 0.07);
}

.link-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(234, 84, 85, 0.12);
  color: #ea5455;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  font-weight: 700;
  font-size: 0.82rem;
  color: #0f172a;
}

.info-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 14px;
  padding: 1rem;
  background: #fff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
}

.info-card__title {
  font-weight: 800;
  font-size: 1rem;
  color: #0f172a;
  margin-bottom: 0.35rem;
}

.section-panel {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 14px;
  padding: 0.75rem 1rem;
  background: #fff;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.04);
  margin-top: 0.75rem;
}

.related-solutions-panel {
  background: radial-gradient(
      circle at 0% 0%,
      rgba(234, 84, 85, 0.05),
      transparent 55%
    ),
    #fff;
}

.child-product-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  background: #fff;
  padding: 0.75rem;
  min-height: 210px;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
}

.child-product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.1);
}

.child-product-thumb-wrap {
  width: 100%;
  height: 108px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.child-product-thumb {
  width: auto;
  max-width: 78px;
  max-height: 78px;
  height: auto;
  object-fit: contain;
  display: block;
}

.child-product-thumb-empty {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #eef2f7;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.child-product-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #18253d;
  line-height: 1.35;
  min-height: 2.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.child-product-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: auto;
}

.child-detail-btn {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 999px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.child-empty-state {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.7rem 0.8rem;
  border-radius: 10px;
  border: 1px dashed rgba(15, 23, 42, 0.25);
  background: #f8fafc;
  color: #475569;
}

.testimonial-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.testimonial-item {
  display: flex;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #fff;
}

.testimonial-item__avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #b91c1c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  overflow: hidden;
}

.testimonial-item__logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.testimonial-item__body {
  flex: 1;
  min-width: 0;
}

.testimonial-item__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.testimonial-item__name {
  font-weight: 700;
  color: #1f2937;
  font-size: 0.9rem;
}

.testimonial-item__date {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 0.2rem;
}

.testimonial-item__message {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #334155;
  line-height: 1.55;
}
</style>

<style lang="scss" scoped>
.container-skeleton {
  width: 100%;
}
.skeleton-box {
  display: inline-block;
  height: 1em;
  position: relative;
  overflow: hidden;
  background-color: #dddbdd;
  border-radius: 8px;

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(#fff, 0) 0,
      rgba(#fff, 0.2) 20%,
      rgba(#fff, 0.5) 60%,
      rgba(#fff, 0)
    );
    animation: shimmer 2s infinite;
    content: '';
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }
}
</style>

<style>
@media (max-width: 991px) {
  .blog-category-title img {
    width: 100% !important;
    height: auto !important;
    max-width: 100%;
  }
}

.sidebar-testimonial .carousel-inner {
  min-height: 300px;
}

.sidebar-testimonial .carousel-caption {
  position: static !important;
  right: auto !important;
  left: auto !important;
  bottom: auto !important;
  padding: 0.5rem 0.75rem 2rem !important;
  color: inherit !important;
  text-shadow: none !important;
}

.sidebar-testimonial .testimonial-slide {
  min-height: 260px !important;
  justify-content: flex-start !important;
  padding-top: 0.25rem !important;
}

@media (max-width: 768px) {
  .sidebar-testimonial .carousel-inner {
    min-height: 290px;
  }

  .sidebar-testimonial .testimonial-slide {
    min-height: 250px !important;
  }
}
</style>
